import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';
import {
  selectBundlePagesData,
  selectBundleRetrieveActivePageIndex,
  selectBundleRetrieveBasePageId,
  selectBundleRetrievePageIds,
  selectBundleRetrievePageSelectedCheckboxes,
  selectSharedDataActiveBundleType,
  setBundleRetrieveActivePageIndex,
  setBundleRetrievePageSelectedCheckboxes,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { BundleItemDataExtended } from '@/api';
import { Checkbox } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';

const ITEM_FOOTER_HEIGHT = 24;

export const RetrievePagesDialogThumbnailsList = () => {
  const dispatch = useAppDispatch();

  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const listIndexRef = useRef<number>(0);

  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundlePagesData = useAppSelector(selectBundlePagesData);

  const bundleRetrievePageIds = useAppSelector(selectBundleRetrievePageIds);
  const bundleRetrieveActivePageIndex = useAppSelector(selectBundleRetrieveActivePageIndex);
  const bundleRetrieveBasePageId = useAppSelector(selectBundleRetrieveBasePageId);
  const bundleRetrievePageSelectedCheckboxes = useAppSelector(selectBundleRetrievePageSelectedCheckboxes);

  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const retrievePagesData = useMemo(() => {
    if (!Array.isArray(bundleRetrievePageIds) || !bundlePagesData) return [];

    return bundleRetrievePageIds.reduce<BundleItemDataExtended[]>((acc, pageId) => {
      const pageData = bundlePagesData.get(pageId);
      if (pageData) {
        acc.push(pageData);
      } else {
        console.warn(`Page data for pageId ${pageId} not found in bundlePagesData`);
      }
      return acc;
    }, []);
  }, [bundleRetrievePageIds, bundlePagesData]);

  const virtualizer = useVirtualizer({
    count: retrievePagesData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index: number) => retrievePagesData[index].pageDimensions.height + ITEM_FOOTER_HEIGHT,
    horizontal: false,
    overscan: 10,
  });

  const items: VirtualItem[] = virtualizer.getVirtualItems();

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight);
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (bundleRetrieveActivePageIndex) {
      if (
        bundleRetrieveActivePageIndex >= 0 &&
        bundleRetrieveActivePageIndex < retrievePagesData.length &&
        listIndexRef.current !== bundleRetrieveActivePageIndex
      ) {
        console.log(`Thumbnails scroll to index: ${bundleRetrieveActivePageIndex}`);
        virtualizer.scrollToIndex(bundleRetrieveActivePageIndex, {
          align: 'center',
          behavior: 'auto',
        });
        listIndexRef.current = bundleRetrieveActivePageIndex;
      } else {
        console.warn(`Wrong index: ${bundleRetrieveActivePageIndex}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleRetrieveActivePageIndex]);

  const onImageClickHandle = useCallback(
    (newPage: number) => {
      if (activeBundleType && newPage !== bundleRetrieveActivePageIndex) {
        listIndexRef.current = newPage;
        dispatch(
          setBundleRetrieveActivePageIndex({
            key: activeBundleType,
            data: newPage,
          }),
        );
      }
    },
    [activeBundleType, bundleRetrieveActivePageIndex, dispatch],
  );

  const handleCheckboxToggle = useCallback(
    (pageId: string) => {
      if (!activeBundleType) return;
      const selected = bundleRetrievePageSelectedCheckboxes;
      const isSelected = selected.includes(pageId);
      let newSelected: string[];
      if (isSelected) {
        newSelected = selected.filter((id) => id !== pageId);
      } else {
        newSelected = [...selected, pageId];
      }
      dispatch(
        setBundleRetrievePageSelectedCheckboxes({
          key: activeBundleType,
          data: newSelected,
        }),
      );
    },
    [activeBundleType, bundleRetrievePageSelectedCheckboxes, dispatch],
  );

  return (
    <div ref={containerRef} className={`relative h-full w-full max-w-full`}>
      <div
        ref={parentRef}
        className="w-full max-w-full overflow-x-hidden overflow-y-auto"
        style={{ height: `${containerHeight}px`, width: `${containerWidth}px` }}
      >
        <div
          className="relative flex w-full max-w-full flex-col"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
          }}
        >
          <div
            className="absolute top-0 left-0 w-full max-w-full"
            style={{
              transform: `translateY(${items[0]?.start ?? 0}px)`,
            }}
          >
            {items.map((virtualItem) => {
              const pageData = retrievePagesData[virtualItem.index];
              const isCurrentPageText = pageData.pageId === bundleRetrieveBasePageId;
              const isBlankPageText = false;

              return (
                <div
                  key={virtualItem.key}
                  className="relative w-full max-w-full flex-shrink-0 pr-[12px] pl-[40px]"
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                  data-testid={pageData.pageId}
                >
                  <div className="absolute top-0 left-[12px]">
                    {!isCurrentPageText && (
                      <Checkbox
                        type="checkbox"
                        checked={bundleRetrievePageSelectedCheckboxes.includes(pageData.pageId)}
                        onChange={() => handleCheckboxToggle(pageData.pageId)}
                      />
                    )}
                  </div>
                  <div
                    className={classNames('relative w-full max-w-full rounded-sm p-1.5', {
                      'bg-fletchers-green-100': bundleRetrieveActivePageIndex === virtualItem.index,
                    })}
                    onClick={() => {
                      onImageClickHandle(virtualItem.index);
                    }}
                  >
                    {(isCurrentPageText || isBlankPageText) && (
                      <>
                        {isCurrentPageText && (
                          <div className="bg-fletchers-gray-175 absolute inset-1.5 z-10 flex items-center justify-center rounded-sm opacity-60" />
                        )}
                        <div
                          className={classNames(
                            'bg-fletchers-white',
                            'text-center text-[12px]/[16px] font-[400]',
                            'rounded-sm p-[6px] text-nowrap',
                            'absolute top-1/2 left-1/2 z-20 transform-[translate(-50%,-50%)]',
                          )}
                        >
                          {isCurrentPageText && <div>Current Page</div>}
                          {isBlankPageText && <div className="text-fletchers-blue-500">Blank</div>}
                        </div>
                      </>
                    )}

                    <img
                      src={pageData.thumbnailUri}
                      // src={'https://picsum.photos/500/700'}
                      alt={`Thumbnail for ${pageData.documentName}`}
                      className={classNames(
                        'border-fletchers-gray-200 bg-fletchers-gray-50 border',
                        'block h-auto max-w-full rounded-sm',
                      )}
                      width={pageData.pageDimensions.width}
                      height={pageData.pageDimensions.height}
                    />
                  </div>
                  <div
                    className={classNames('flex items-center justify-center', 'text-[12px]/[16px] font-[400]')}
                    style={{
                      minHeight: `${ITEM_FOOTER_HEIGHT}px`,
                      height: `${ITEM_FOOTER_HEIGHT}px`,
                      maxHeight: `${ITEM_FOOTER_HEIGHT}px`,
                    }}
                  >
                    <div>{pageData.originalPageNumber}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
